import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { submissionsAPI, challengesAPI } from '../../services/api'
import {
  DocumentTextIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  StarIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { format } from 'date-fns'
import { toast } from 'react-hot-toast'

const statusIcons = {
  submitted: ClockIcon,
  under_review: ExclamationTriangleIcon,
  evaluated: CheckCircleIcon,
}

const statusColors = {
  submitted: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  evaluated: 'bg-green-100 text-green-800',
}

export default function AdminSubmissions() {
  const [searchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [challengeFilter, setChallengeFilter] = useState('')
  const [showEvaluationModal, setShowEvaluationModal] = useState(false)
  const [selectedSubmission, setSelectedSubmission] = useState(null)
  const [evaluationData, setEvaluationData] = useState({ score: '', feedback: '' })
  
  const queryClient = useQueryClient()

  // Fetch all submissions (admin can see all)
  const { data: submissionsData, isLoading, error } = useQuery(
    'admin-all-submissions',
    async () => {
      // Since there's no admin-specific endpoint, we'll use the existing one
      // In a real implementation, you'd want an admin endpoint that returns all submissions
      const response = await submissionsAPI.getMySubmissions({ limit: 1000 })
      return response.data.submissions
    }
  )

  // Fetch challenges for filter
  const { data: challengesData } = useQuery(
    'admin-challenges-for-filter',
    () => challengesAPI.getAll(),
    {
      select: (response) => response.data.challenges
    }
  )

  // Evaluate submission mutation
  const evaluateMutation = useMutation(
    ({ submissionId, evaluation }) => submissionsAPI.evaluate(submissionId, evaluation),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-all-submissions')
        setShowEvaluationModal(false)
        setSelectedSubmission(null)
        setEvaluationData({ score: '', feedback: '' })
        toast.success('Submission evaluated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to evaluate submission')
      }
    }
  )

  const handleEvaluate = (submission) => {
    setSelectedSubmission(submission)
    setEvaluationData({
      score: submission.score || '',
      feedback: submission.feedback || ''
    })
    setShowEvaluationModal(true)
  }

  const handleSubmitEvaluation = (e) => {
    e.preventDefault()
    if (!evaluationData.score || evaluationData.score < 0 || evaluationData.score > 100) {
      toast.error('Please enter a valid score between 0 and 100')
      return
    }

    evaluateMutation.mutate({
      submissionId: selectedSubmission.id,
      evaluation: {
        score: parseFloat(evaluationData.score),
        feedback: evaluationData.feedback
      }
    })
  }

  // Filter submissions
  const filteredSubmissions = submissionsData?.filter(submission => {
    const matchesSearch = !searchTerm || 
      submission.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.challenge_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = !statusFilter || submission.status === statusFilter
    const matchesChallenge = !challengeFilter || submission.challenge_id === parseInt(challengeFilter)
    
    return matchesSearch && matchesStatus && matchesChallenge
  }) || []

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading submissions</h3>
          <p className="text-gray-500">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
            Submission Management
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Review, evaluate, and manage all submissions across all challenges
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="search"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Search submissions, challenges, or users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="submitted">Submitted</option>
                  <option value="under_review">Under Review</option>
                  <option value="evaluated">Evaluated</option>
                </select>
              </div>

              {/* Challenge Filter */}
              <div>
                <label htmlFor="challenge" className="block text-sm font-medium text-gray-700 mb-1">
                  Challenge
                </label>
                <select
                  id="challenge"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  value={challengeFilter}
                  onChange={(e) => setChallengeFilter(e.target.value)}
                >
                  <option value="">All Challenges</option>
                  {challengesData?.map((challenge) => (
                    <option key={challenge.id} value={challenge.id}>
                      {challenge.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Submissions ({filteredSubmissions.length})
              </h3>
            </div>

            {filteredSubmissions.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter || challengeFilter
                    ? 'Try adjusting your filters or search terms.'
                    : 'No submissions have been made yet.'
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submission
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Challenge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submitted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSubmissions.map((submission) => {
                      const StatusIcon = statusIcons[submission.status]
                      return (
                        <tr key={submission.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {submission.title}
                              </div>
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {submission.description}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {submission.challenge_title || 'Unknown Challenge'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <div className="text-sm text-gray-900">
                                {submission.user_name || 'Unknown User'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <StatusIcon className="h-4 w-4 mr-2" />
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[submission.status]}`}>
                                {submission.status.replace('_', ' ')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {submission.score ? (
                                <div className="flex items-center">
                                  <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                                  {submission.score}
                                </div>
                              ) : (
                                <span className="text-gray-400">Not scored</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {format(new Date(submission.created_at), 'MMM dd, yyyy')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/admin/submissions/${submission.id}`}
                                className="text-purple-600 hover:text-purple-900"
                                title="View Submission"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Link>
                              {submission.status !== 'evaluated' && (
                                <button
                                  onClick={() => handleEvaluate(submission)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Evaluate Submission"
                                >
                                  <StarIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Evaluation Modal */}
      {showEvaluationModal && selectedSubmission && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Evaluate Submission
                </h3>
                <button
                  onClick={() => setShowEvaluationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <h4 className="font-medium text-gray-900">{selectedSubmission.title}</h4>
                <p className="text-sm text-gray-500">{selectedSubmission.challenge_title}</p>
              </div>

              <form onSubmit={handleSubmitEvaluation}>
                <div className="mb-4">
                  <label htmlFor="score" className="block text-sm font-medium text-gray-700 mb-1">
                    Score (0-100)
                  </label>
                  <input
                    type="number"
                    id="score"
                    min="0"
                    max="100"
                    step="0.1"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={evaluationData.score}
                    onChange={(e) => setEvaluationData(prev => ({ ...prev, score: e.target.value }))}
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                    Feedback (Optional)
                  </label>
                  <textarea
                    id="feedback"
                    rows={4}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={evaluationData.feedback}
                    onChange={(e) => setEvaluationData(prev => ({ ...prev, feedback: e.target.value }))}
                    placeholder="Provide feedback on the submission..."
                  />
                </div>

                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEvaluationModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={evaluateMutation.isLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {evaluateMutation.isLoading ? 'Evaluating...' : 'Submit Evaluation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}